import React, { useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';

const LOGO_URL = 'https://cdn.showgear.online/logos/logo-b.svg';

const GOOGLE_REVIEW_MOBILE =
  'https://www.google.com/search?q=show+gear+productions+reviews&si=ALGXSlYWsjFmaPLJzT6890bFu84dn80YhtIs5Az7ElrpwzXPNcEB-EH3v796DVRe0oOPQMcW--Aj50b6qHrvkf_kMc2zuDCISD4TKtclvmmewmCgF7ODq2WFJTOlwo2cSGqVj_Y4A1ty5eh5TgsCCcce3ywjmLN56g%3D%3D&ictx=1&ved=2ahUKEwjEnOGT8P2CAxXqsFYBHTA5B-8QyNoBKAF6BAgREAg#lrd=0x80dceba1b81f5db7:0x2339bcd58af3c4fa,3,,,,';
const GOOGLE_REVIEW_DESKTOP =
  'https://www.google.com/search?q=show+gear+productions#lrd=0x80dceba1b81f5db7:0x2339bcd58af3c4fa,3,,,,';

const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL || '';

const CTA_LINKS = [
  { label: 'View Portfolio', href: 'https://showgear.com/' },
  { label: 'Contact Show Gear', href: 'https://showgear.com/contact' },
  { label: 'Explore Services', href: 'https://showgear.com' },
];

type Step = 'rating' | 'feedback' | 'review' | 'thankyou';

type FeedbackForm = {
  name: string;
  email: string;
  phone: string;
  booth: string;
  notes: string;
  whatWeDidRight: string;
  whatToDoBetter: string;
  whatWasAmazing: string;
  whoToRecognize: string;
};

const DEFAULT_FORM: FeedbackForm = {
  name: '',
  email: '',
  phone: '',
  booth: '',
  notes: '',
  whatWeDidRight: '',
  whatToDoBetter: '',
  whatWasAmazing: '',
  whoToRecognize: '',
};

const STAR_DESCRIPTIONS = ['poor', 'not great', 'ok', 'great', 'excellent'];

const StarRating: React.FC<{ value: number; onSelect: (value: number) => void }> = ({
  value,
  onSelect,
}) => {
  const [hovered, setHovered] = useState(0);
  const stars = [1, 2, 3, 4, 5];

  return (
    <div className="star-rating" role="radiogroup" aria-label="Rate your experience">
      {stars.map((star) => {
        const isFilled = star <= (hovered || value);
        return (
          <button
            key={star}
            className={`star ${isFilled ? 'star--filled' : ''}`}
            onClick={() => onSelect(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            aria-label={`Rate ${star} star${star > 1 ? 's' : ''}, ${STAR_DESCRIPTIONS[star - 1]}`}
            aria-checked={value === star}
            role="radio"
            type="button"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 .587l3.668 7.431 8.213 1.194-5.94 5.79 1.402 8.17L12 18.896l-7.343 3.876 1.402-8.17-5.94-5.79 8.213-1.194z" />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

const NeonBackdrop: React.FC = () => (
  <div className="backdrop" aria-hidden="true">
    {['backdrop__corner--tl', 'backdrop__corner--br'].map((corner) => (
      <div key={corner} className={`backdrop__corner ${corner}`}>
        <span className="beam beam--glow" />
        <span className="beam beam--hairline" />
        <span className="beam beam--purple" />
        <span className="beam beam--cyan" />
        <span className="beam beam--blue" />
        <span className="beam beam--hairline-2" />
        <span className="dots" />
        <svg className="trace" viewBox="0 0 200 200" preserveAspectRatio="none">
          <path d="M0 168 H64 L118 114 V44 L146 16" />
        </svg>
      </div>
    ))}
  </div>
);

const ALL_STEPS: { id: Step; label: string }[] = [
  { id: 'rating', label: 'Rate' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'review', label: 'Review' },
  { id: 'thankyou', label: 'Finish' },
];

const StepIndicator: React.FC<{ current: Step; rating: number }> = ({
  current,
  rating,
}) => {
  const reviewIsReachable = rating === 0 || rating === 5;
  const currentIndex = ALL_STEPS.findIndex((s) => s.id === current);

  return (
    <ol className="step-indicator">
      {ALL_STEPS.map((step, index) => {
        const isActive = step.id === current;
        const isComplete = currentIndex > index;
        const isDimmed = step.id === 'review' && !reviewIsReachable;
        return (
          <li
            key={step.id}
            className={[
              'step',
              isActive ? 'step--active' : '',
              isComplete ? 'step--complete' : '',
              isDimmed ? 'step--dimmed' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className="step__index">{index + 1}</span>
            <span className="step__label">{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
};

type FieldErrors = Partial<Record<keyof FeedbackForm, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const App: React.FC = () => {
  const [step, setStep] = useState<Step>('rating');
  const [rating, setRating] = useState<number>(0);
  const [form, setForm] = useState<FeedbackForm>(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [googleOpened, setGoogleOpened] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const ratingLabel = useMemo(() => {
    if (!rating) {
      return 'Tap a star to begin';
    }
    const labels = ['Could be better', 'Not great', 'It was ok', 'Great', 'Amazing'];
    return labels[rating - 1];
  }, [rating]);

  const submitToWebhook = async (payload: Record<string, string | number>) => {
    if (!N8N_WEBHOOK_URL) {
      console.warn('N8N webhook URL not configured');
      return;
    }
    try {
      await axios.post(N8N_WEBHOOK_URL, {
        name: payload.name ?? '',
        email: payload.email ?? '',
        phone: payload.phone ?? '',
        booth: payload.booth ?? '',
        rating: payload.rating ?? rating,
        review: payload.review ?? payload.notes ?? '',
        whatWeDidRight: payload.whatWeDidRight ?? '',
        whatToDoBetter: payload.whatToDoBetter ?? '',
        whatWasAmazing: payload.whatWasAmazing ?? '',
        whoToRecognize: payload.whoToRecognize ?? '',
        // 1 only on the "I Shared My Review" ping. The n8n workflow keys off
        // this to run Google verification instead of filing a new review.
        reviewPosted: payload.reviewPosted ?? 0,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Error submitting to webhook', err);
      throw err;
    }
  };

  const handleRatingSelect = (value: number) => {
    setRating(value);
    setError(null);
    setStep('feedback');
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name as keyof FeedbackForm]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name as keyof FeedbackForm];
        return next;
      });
    }
  };

  const validateForm = (): FieldErrors => {
    const errors: FieldErrors = {};
    const requireText = (key: keyof FeedbackForm, label: string) => {
      if (!form[key].trim()) errors[key] = `${label} is required.`;
    };
    const requireEmail = (key: keyof FeedbackForm) => {
      const value = form[key].trim();
      if (!value) errors[key] = 'Email is required so we can follow up.';
      else if (!EMAIL_PATTERN.test(value)) errors[key] = 'Please enter a valid email address.';
    };
    const validateOptionalEmail = (key: keyof FeedbackForm) => {
      const value = form[key].trim();
      if (value && !EMAIL_PATTERN.test(value)) {
        errors[key] = 'Please enter a valid email address.';
      }
    };
    if (rating <= 3) {
      requireText('name', 'Full name');
      requireEmail('email');
      requireText('notes', 'A short description');
    } else if (rating === 4) {
      requireText('whatWeDidRight', 'This field');
      requireText('whatToDoBetter', 'This field');
      validateOptionalEmail('email');
    } else if (rating === 5) {
      requireText('whatWasAmazing', 'This field');
      validateOptionalEmail('email');
    }
    return errors;
  };

  const handleFeedbackSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setLoading(true);
    setError(null);

    try {
      const review =
        rating === 5
          ? form.whatWasAmazing
          : rating === 4
          ? `What we did right: ${form.whatWeDidRight}\nWhat to improve: ${form.whatToDoBetter}`
          : form.notes;
      await submitToWebhook({ ...form, rating, review });
      if (rating === 5) {
        setStep('review');
      } else if (rating === 4) {
        setFeedbackMessage('Thanks for your feedback! Your input helps us keep raising the bar.');
        setForm(DEFAULT_FORM);
        setStep('thankyou');
      } else {
        setFeedbackMessage('Thank you for taking the time to help us improve. A Show Gear specialist will follow up shortly.');
        setForm(DEFAULT_FORM);
        setStep('thankyou');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAndReview = async () => {
    const reviewText = form.whatWasAmazing || '';
    if (reviewText) {
      try {
        await navigator.clipboard.writeText(reviewText);
        setCopied(true);
        setCopyFailed(false);
        setTimeout(() => setCopied(false), 3000);
      } catch {
        setCopyFailed(true);
      }
    }
    window.open(reviewLink, '_blank', 'noreferrer');
    setGoogleOpened(true);
  };

  const handleReviewContinue = async () => {
    try {
      await submitToWebhook({
        name: form.name,
        email: form.email,
        rating,
        review: form.whatWasAmazing,
        reviewPosted: 1,
      });
    } catch {
      // Non-blocking — proceed to thank you regardless
    }
    setFeedbackMessage('We appreciate you sharing the love!');
    setForm(DEFAULT_FORM);
    setGoogleOpened(false);
    setCopyFailed(false);
    setStep('thankyou');
  };

  const reviewLink = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return GOOGLE_REVIEW_DESKTOP;
    }
    const isMobile = /Mobi|Android|iPhone/i.test(navigator.userAgent);
    return isMobile ? GOOGLE_REVIEW_MOBILE : GOOGLE_REVIEW_DESKTOP;
  }, []);

  return (
    <div className="app-shell">
      <NeonBackdrop />
      <main className="experience-card">
        <div className="logo-wrap">
          <img src={LOGO_URL} alt="Show Gear Productions" className="logo" />
        </div>
        <StepIndicator current={step} rating={rating} />

        {step === 'rating' && (
          <section className="panel">
            <p className="eyebrow">How was your experience?</p>
            <h1>Share your Show Gear moment</h1>
            <p className="subcopy">
              Your feedback keeps our productions bold and unforgettable. Tap a star to get started.
            </p>
            <StarRating value={rating} onSelect={handleRatingSelect} />
            <p className={`rating-label ${rating === 0 ? 'rating-label--empty' : ''}`}>{ratingLabel}</p>
            {error && <p className="error-text">{error}</p>}
          </section>
        )}

        {step === 'feedback' && rating <= 3 && (
          <section className="panel">
            <p className="eyebrow">We want to make it right</p>
            <h1>Tell us what happened</h1>
            <p className="subcopy">
              Your feedback goes straight to our leadership team. Please leave a way to reach you so we can follow up.
            </p>
            <form className="feedback-form" onSubmit={handleFeedbackSubmit} noValidate>
              <label>
                Full name <span className="required-tag">(required)</span>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  placeholder="Jane Smith"
                  aria-invalid={Boolean(fieldErrors.name)}
                  aria-describedby={fieldErrors.name ? 'err-name' : undefined}
                />
                {fieldErrors.name && <span id="err-name" className="field-error">{fieldErrors.name}</span>}
              </label>
              <label>
                Email <span className="required-tag">(required)</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleFormChange}
                  placeholder="you@email.com"
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'err-email' : undefined}
                />
                {fieldErrors.email && <span id="err-email" className="field-error">{fieldErrors.email}</span>}
              </label>
              <label>
                Phone <span className="optional-tag">(optional)</span>
                <input name="phone" value={form.phone} onChange={handleFormChange} placeholder="(555) 123-4567" />
              </label>
              <label>
                Booth or event reference <span className="optional-tag">(optional)</span>
                <input name="booth" value={form.booth} onChange={handleFormChange} placeholder="CES booth #123" />
              </label>
              <label>
                How can we do better? <span className="required-tag">(required)</span>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleFormChange}
                  placeholder="Share the details so we can improve."
                  rows={4}
                  aria-invalid={Boolean(fieldErrors.notes)}
                  aria-describedby={fieldErrors.notes ? 'err-notes' : undefined}
                />
                {fieldErrors.notes && <span id="err-notes" className="field-error">{fieldErrors.notes}</span>}
              </label>
              {error && <p className="error-text">{error}</p>}
              <button className="primary" type="submit" disabled={loading}>
                {loading ? 'Sending…' : 'Send Feedback'}
              </button>
            </form>
          </section>
        )}

        {step === 'feedback' && rating === 4 && (
          <section className="panel">
            <p className="eyebrow">Almost perfect</p>
            <h1>Help us go from great to amazing</h1>
            <p className="subcopy">
              We're glad you had a good experience. Tell us what stood out and where we can level up.
            </p>
            <form className="feedback-form" onSubmit={handleFeedbackSubmit} noValidate>
              <label>
                What did we do right? <span className="required-tag">(required)</span>
                <textarea
                  name="whatWeDidRight"
                  value={form.whatWeDidRight}
                  onChange={handleFormChange}
                  placeholder="What stood out about your experience?"
                  rows={3}
                  aria-invalid={Boolean(fieldErrors.whatWeDidRight)}
                  aria-describedby={fieldErrors.whatWeDidRight ? 'err-whatWeDidRight' : undefined}
                />
                {fieldErrors.whatWeDidRight && (
                  <span id="err-whatWeDidRight" className="field-error">{fieldErrors.whatWeDidRight}</span>
                )}
              </label>
              <label>
                What can we do better? <span className="required-tag">(required)</span>
                <textarea
                  name="whatToDoBetter"
                  value={form.whatToDoBetter}
                  onChange={handleFormChange}
                  placeholder="Any ideas on how we can improve?"
                  rows={3}
                  aria-invalid={Boolean(fieldErrors.whatToDoBetter)}
                  aria-describedby={fieldErrors.whatToDoBetter ? 'err-whatToDoBetter' : undefined}
                />
                {fieldErrors.whatToDoBetter && (
                  <span id="err-whatToDoBetter" className="field-error">{fieldErrors.whatToDoBetter}</span>
                )}
              </label>
              <label>
                Booth or event reference <span className="optional-tag">(optional)</span>
                <input name="booth" value={form.booth} onChange={handleFormChange} placeholder="CES booth #123" />
              </label>
              <label>
                Full name <span className="optional-tag">(optional)</span>
                <input name="name" value={form.name} onChange={handleFormChange} placeholder="Jane Smith" />
              </label>
              <label>
                Email <span className="optional-tag">(optional)</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleFormChange}
                  placeholder="you@email.com"
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'err-email-4' : undefined}
                />
                {fieldErrors.email && <span id="err-email-4" className="field-error">{fieldErrors.email}</span>}
              </label>
              {error && <p className="error-text">{error}</p>}
              <button className="primary" type="submit" disabled={loading}>
                {loading ? 'Sending…' : 'Send Feedback'}
              </button>
            </form>
          </section>
        )}

        {step === 'feedback' && rating === 5 && (
          <section className="panel">
            <p className="eyebrow">Amazing</p>
            <h1>We love hearing that!</h1>
            <p className="subcopy">
              Tell us what made your experience special — your words help us celebrate our team and keep delivering our best.
            </p>
            <form className="feedback-form" onSubmit={handleFeedbackSubmit} noValidate>
              <label>
                What made your experience amazing? <span className="required-tag">(required)</span>
                <textarea
                  name="whatWasAmazing"
                  value={form.whatWasAmazing}
                  onChange={handleFormChange}
                  placeholder="Share what stood out — we'll copy this into your Google review."
                  rows={4}
                  aria-invalid={Boolean(fieldErrors.whatWasAmazing)}
                  aria-describedby={fieldErrors.whatWasAmazing ? 'err-whatWasAmazing' : undefined}
                />
                {fieldErrors.whatWasAmazing && (
                  <span id="err-whatWasAmazing" className="field-error">{fieldErrors.whatWasAmazing}</span>
                )}
              </label>
              <label>
                Anyone on our team you'd like to recognize? <span className="optional-tag">(optional)</span>
                <input name="whoToRecognize" value={form.whoToRecognize} onChange={handleFormChange} placeholder="Shout out a crew member or team" />
              </label>
              <label>
                Booth or event reference <span className="optional-tag">(optional)</span>
                <input name="booth" value={form.booth} onChange={handleFormChange} placeholder="CES booth #123" />
              </label>
              <label>
                Full name <span className="optional-tag">(optional)</span>
                <input name="name" value={form.name} onChange={handleFormChange} placeholder="Jane Smith" />
              </label>
              <label>
                Email <span className="optional-tag">(optional)</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleFormChange}
                  placeholder="you@email.com"
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'err-email-5' : undefined}
                />
                {fieldErrors.email && <span id="err-email-5" className="field-error">{fieldErrors.email}</span>}
              </label>
              {error && <p className="error-text">{error}</p>}
              <button className="primary" type="submit" disabled={loading}>
                {loading ? 'Sending…' : 'Continue to Google Review'}
              </button>
            </form>
          </section>
        )}

        {step === 'review' && (
          <section className="panel">
            <p className="eyebrow">One More Step</p>
            <h1>Share the love on Google</h1>
            <p className="subcopy">
              Your feedback has been saved. Now share it publicly — we've made it easy.
              Tap the button below to copy your review and open Google so you can paste it right in.
            </p>
            {form.whatWasAmazing && (
              <div className="review-preview">
                <p className="review-preview__text">"{form.whatWasAmazing}"</p>
              </div>
            )}
            <div className="review-actions">
              <button type="button" className="primary full-width" onClick={handleCopyAndReview}>
                {copied ? 'Copied — Opening Google…' : 'Copy My Review & Open Google'}
              </button>
              {!googleOpened && (
                <p className="subcopy hint-text">
                  Your review text will be copied to your clipboard. Just paste it into the Google review form — no need to retype anything!
                </p>
              )}
              {googleOpened && !copyFailed && (
                <div className="paste-guide">
                  <p className="paste-guide__step">On Google, tap the stars then paste your review:</p>
                  <p className="paste-guide__shortcut">
                    <span>Mac:</span> <kbd>⌘ Cmd + V</kbd>&nbsp;&nbsp;<span>Windows / Android:</span> <kbd>Ctrl + V</kbd>
                  </p>
                </div>
              )}
              {copyFailed && (
                <div className="copy-fallback">
                  <p className="copy-fallback__label">Copy failed — select and copy the text below:</p>
                  <textarea
                    className="copy-fallback__text"
                    readOnly
                    value={form.whatWasAmazing}
                    rows={4}
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    className="secondary"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(form.whatWasAmazing);
                        setCopied(true);
                        setCopyFailed(false);
                        setTimeout(() => setCopied(false), 3000);
                      } catch {
                        // Stay in fallback mode
                      }
                    }}
                  >
                    Try Copying Again
                  </button>
                </div>
              )}
              {error && <p className="error-text">{error}</p>}
              <div className="cta-grid">
                {CTA_LINKS.map((cta) => (
                  <a key={cta.href} className="ghost" href={cta.href} target="_blank" rel="noreferrer">
                    {cta.label}
                  </a>
                ))}
              </div>
              {googleOpened && (
                <button type="button" className="secondary" onClick={handleReviewContinue}>
                  I Shared My Review
                </button>
              )}
            </div>
          </section>
        )}

        {step === 'thankyou' && (
          <section className="panel">
            <p className="eyebrow">Thank you</p>
            <h1>We appreciate you</h1>
            <p className="subcopy">{feedbackMessage || 'Your support means the world to our crew.'}</p>
            <div className="cta-grid">
              {CTA_LINKS.map((cta) => (
                <a key={cta.href} className="primary" href={cta.href} target="_blank" rel="noreferrer">
                  {cta.label}
                </a>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default App;
